import {useEffect, useMemo, useRef} from 'react';

import {useListbox} from '@pb/kit-component-listbox';
import {useControlledState} from '@pb/kit-hooks-use-controlled-state';
import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';


import type {AutosuggestListProps} from './autosuggest-list.component';
import type {OpenChangeReason} from '@pb/kit-component-floating';
import type {UseListboxProps} from '@pb/kit-component-listbox';
import type {ComponentProps, FormEvent, KeyboardEvent} from 'react';

type SetShown = (open: boolean, event?: Event, reason?: OpenChangeReason | 'item-select' | 'input-change') => void;

type _UseListboxProps = Omit<UseListboxProps, 'setShown'>;

export interface UseAutosuggestProps extends _UseListboxProps {
    /**
     * Called when the user selects a suggestion. Receives the `value` from the
     * `<AutosuggestItem>` and the trigger type (`"MOUSE_DOWN"` or `"ENTER"`).
     */
    onItemSelect?: (itemValue: any, selectType: 'MOUSE_DOWN' | 'ENTER', event?: Event) => void, // eslint-disable-line @typescript-eslint/no-explicit-any
    /**
     * When `true` (default), the listbox closes after an item is selected.
     * Set to `false` to keep the list open for multi-select patterns.
     */
    hideOnItemClick?: boolean,
    /**
     * Controlled setter for the open/close state of the listbox. Receives the
     * new boolean, an optional DOM event, and an optional reason string
     * (e.g. `"item-select"`, `"input-change"`).
     */
    setShown?: SetShown,
    /**
     * When `true` (default), clearing the input value closes the listbox.
     * Set to `false` to keep the list open when the input is cleared.
     */
    isClosedOnClear?: boolean,
}

/**
 * Core logic hook for building a custom autosuggest input. Returns
 * `getAnchorProps` (spread onto the `<Input>`), `getAutosuggestListProps`
 * (spread onto `<AutosuggestList>`), and `anchorRef`.
 *
 * Use this hook directly when `<Autosuggest>` does not meet your layout
 * requirements and you need to compose the anchor and listbox separately.
 */
export const useAutosuggest = ({
    shown: externalShown,
    setShown: externalSetShown,
    onItemSelect: onItemSelectProp,
    hideOnItemClick = true,
    isClosedOnClear = true,
    listNavigationProps,
    ...restProps
}: UseAutosuggestProps) => {
    const [shown, setShown] = useControlledState(externalShown, externalSetShown, false);

    const itemsValuesOrder = useRef<Array<string>>([]);

    const onItemSelect: typeof onItemSelectProp = useCallbackWithRef((value, selectType, event) => {
        if (hideOnItemClick) {
            setShown(false, event, 'item-select');
        }
        onItemSelectProp?.(value, selectType);
    });

    const {
        getAnchorProps,
        setActiveIndex,
        getListBoxProps,
        anchorRef,
        getContext,
    } = useListbox({
        clickEnabled: false,
        shown,
        setShown,
        listNavigationProps: {
            focusItemOnOpen: false,
            ...listNavigationProps,
        },
        ...restProps,
    });

    const childrenCount = useRef(0);

    const onMountItem = useCallbackWithRef(() => {
        childrenCount.current += 1;
        if (listNavigationProps?.focusItemOnOpen && childrenCount.current === 1) {
            setActiveIndex(0);
        }
    });

    const onUnmountItem = useCallbackWithRef(() => {
        childrenCount.current -= 1;
    });

    const contextValue = useMemo(() => ({
        onItemSelect,
        onMountItem,
        onUnmountItem,
        itemsValuesOrder,
    }), [onItemSelect, onUnmountItem, onMountItem]);


    const _getAnchorProps: typeof getAnchorProps = props => getAnchorProps({
        ...props,
        onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
            props?.onKeyDown?.(event);
            if (event.key === 'Enter') {
                const {activeIndex} = getListBoxProps();
                if (typeof activeIndex !== 'number' || activeIndex < 0 || !itemsValuesOrder.current[activeIndex]) {
                    return;
                }
                onItemSelect(itemsValuesOrder.current[activeIndex], 'ENTER', event.nativeEvent);
            }
        },
        onChange: (e: FormEvent<HTMLInputElement>) => {
            props?.onChange?.(e);

            const isShown = !isClosedOnClear || !!e.currentTarget.value;

            setShown(isShown, e.nativeEvent, 'input-change');
        },
    });

    const getAutosuggestListProps = (nativeProps?: ComponentProps<'div'>): Omit<AutosuggestListProps, 'children'> => ({
        contextValue,
        ...getListBoxProps(nativeProps),
    });

    useEffect(() => {
        const onFocusOut = (event: Event) => {
            const {relatedTarget} = event as FocusEvent;
            if (
                relatedTarget
                && getContext().refs.floating.current?.contains(relatedTarget as Node)
            ) {
                // Prevent the input to lose focus when an item is clicked
                event.stopPropagation();
            }
        };
        getContext().refs.domReference.current?.addEventListener('focusout', onFocusOut);
        return () => {
            getContext().refs.domReference.current?.removeEventListener('focusout', onFocusOut);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    return {
        anchorRef,
        getAnchorProps: _getAnchorProps,
        getAutosuggestListProps,
    };
};
